import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { parseRoute, type PanelRoute } from '../router.ts';

import './topbar.ts';
import './panel-view.ts';

/**
 * `<exhibition-app>` — top-level shell.
 *
 * Wires the topbar and the central viewing area, listens to `hashchange`,
 * and applies the View Transitions API when the browser supports it so
 * panel switches read as a single coherent moment rather than a flash.
 */
@customElement('exhibition-app')
export class ExhibitionAppElement extends LitElement {
  static override styles = [
    css`
      :host {
        display: grid;
        grid-template-rows: auto 1fr;
        min-block-size: 100dvh;
        background: var(--panel-chassis, #131313);
        color: var(--text-primary, #e8e8e8);
        font-family: var(--font-ui);
      }
    `,
  ];

  @state() private accessor route: PanelRoute | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.#syncRoute();
    window.addEventListener('hashchange', this.#onHashChange);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('hashchange', this.#onHashChange);
  }

  #onHashChange = (): void => {
    // View Transitions wrap the state mutation so the layout swap reads as
    // a single coherent moment rather than two separate paints.
    const apply = (): void => this.#syncRoute();
    if ('startViewTransition' in document) {
      document.startViewTransition(apply);
    } else {
      apply();
    }
  };

  #syncRoute(): void {
    this.route = parseRoute(window.location.hash);
  }

  override render() {
    return html`
      <exhibition-topbar .route=${this.route}></exhibition-topbar>
      <exhibition-panel-view .route=${this.route}></exhibition-panel-view>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'exhibition-app': ExhibitionAppElement;
  }
}
